import React, { useState, useEffect, useContext } from "react";
import { Card, Table, Button, Form, OverlayTrigger, Tooltip, Spinner} from "react-bootstrap";
import NotyfContext from "../contexts/NotyfContext";
import { X, Check, Edit2, Trash2, File} from "react-feather";
import { UserSettingsContext } from "../contexts/UserSettingsContext";
import { useTable, useSortBy, useFilters} from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";

import authenticatedFetch from "../utils/fetch"
import EditableExpires from "./EditableExpires";

import rawPsvTypes from "./psvTypes"
import rawPsvResults from "./psvResults"
import LinkedDocumentsDisplay from "./LinkedDocumentsDisplay";
import LinkedDocumentSelect from "./LinkedDocumentSelect";
import { getDocumentDisplayLabel } from "./linkedDocumentUtils";
import { caseInsensitiveSort } from "./reactTableUtils";
import UsStateSelect from "./UsStateSelect";


const Psv = ({ id, providerId, psvType, state, psvResult, psvDate, notes, documents, refreshList, canEdit, onCancelEdit, startInEditMode=false }) => {
  const [editing, setEditing] = useState(startInEditMode);
  const [localPsvType, setLocalPsvType] = useState(psvType);
  const [localState, setLocalState] = useState(state);
  const [localPsvResult, setLocalPsvResult] = useState(psvResult);
  const [localPsvDate, setLocalPsvDate] = useState(psvDate);
  const [localNotes, setLocalNotes] = useState(notes);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [documentFilenames, setDocumentFilenames] = useState([]);

  const { isH3Admin, authorities, documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);
  
  const notyf = useContext(NotyfContext);

  const setInitialDocumentFilenames = () => {
    let initialDocumentFilenames = []
    let initialSelectedDocuments = []
    if (documents) // undefined if we're excluding the column due to permissions
    {
      for (var i=0; i<documents.length; i++)
      {
        initialDocumentFilenames.push(documents[i].filename);
        initialSelectedDocuments.push({value: documents[i].filename, label: getDocumentDisplayLabel(documents[i])});
      }
    }

    setDocumentFilenames(initialDocumentFilenames);
    setSelectedDocuments(initialSelectedDocuments);
  }

  useEffect(() => {
      setInitialDocumentFilenames();
  }, [id, providerId, documents]);

  useEffect(() => {
    if (!editing) {
      setLocalPsvType(psvType);
      setLocalState(state);
      setLocalPsvResult(psvResult);
      setLocalPsvDate(psvDate);
      setLocalNotes(notes);
      setInitialDocumentFilenames();
    }
  }, [psvType, psvResult, psvDate, notes, documents, state]);

  const handleDocumentChanged = (documents, e) => {
    setSelectedDocuments(documents);

    let newDocumentFilenames = []
    for (var i=0; i<documents.length; i++)
    {
      newDocumentFilenames.push(documents[i].value);
    }

    setDocumentFilenames(newDocumentFilenames);
  }

  function handleDeleteClicked(e) {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this item?"))
    {
      authenticatedFetch({
        method: "DELETE",
        path: "psv/" + id,
        successHandler: function(result) {
          refreshList();
          setDocumentsRefresh(documentsRefresh + 1);
          notyf.open({type: "success", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        },
        errorHandler: function(error) {
          notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
        }
      });
    }
  }

  function handleEditClicked(e) {
    e.preventDefault();
    setEditing(true);
  }

  function cancelEdit(e) {
    e.preventDefault();
    setEditing(false);
    setLocalPsvType(psvType);
    setLocalState(state);
    setLocalPsvResult(psvResult);
    setLocalPsvDate(psvDate);
    setLocalNotes(notes);
    setInitialDocumentFilenames();

    if (onCancelEdit)
    {
      onCancelEdit();
    }
  }

  function saveEdit(e) {
    e.preventDefault();
    if (localPsvType === '' || localPsvType === null || localPsvDate === '' || localPsvDate === null || localPsvResult === '' || localPsvResult === null)
    {
      alert("Please enter a PSV type, date, and result");
      return;
    }

    setEditing(false);

    authenticatedFetch({
      method: id ? "PUT" : "POST",
      requestData: {providerId: parseInt(providerId), psvType: localPsvType, state: localState, psvResult: localPsvResult, psvDate: localPsvDate, notes: localNotes, documentFilenames: documentFilenames},
      path: id ? "psv/" + id  : "psv",
      successHandler: function(result) {
        refreshList();
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: id ? "Updated!" : "Saved!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setEditing(true);
        setLocalPsvType(psvType);
        setLocalState(state);
        setLocalPsvResult(psvResult);
        setLocalPsvDate(psvDate);
        setLocalNotes(notes);
        setInitialDocumentFilenames();
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }


  function handlePsvTypeChanged(e) {
    e.preventDefault();
    setLocalPsvType(e.target.value);
  }

  function handleStateChanged(name, newValue) {
    setLocalState(newValue);
  }

  function handlePsvResultChanged(e) {
    e.preventDefault();
    setLocalPsvResult(e.target.value);
  }

  function handlePsvDateChanged(val, e) {
    e.preventDefault();
    setLocalPsvDate(val);
  }

  function handleNotesChanged(e) {
    e.preventDefault();
    setLocalNotes(e.target.value);
  }

  let psvTypeList = rawPsvTypes.map((psvType, i) => {
    return (
      <option key={i} value={psvType.value}>{psvType.label}</option>
    )
  }, this);

  var selectedPsvTypeLabel = '';
  if (localPsvType !== '')
  {
      for (let i = 0; i < rawPsvTypes.length; i++)
      {
          if (localPsvType === rawPsvTypes[i].value)
          {
            selectedPsvTypeLabel = rawPsvTypes[i].label;
            break;
          }
      }
  }

  const psvResultList = rawPsvResults.map((psvResult, i) => {
    return (
      <option key={i} value={psvResult.value}>{psvResult.label}</option>
    )
  }, this);

  var selectedPsvResultLabel = '';
  if (localPsvResult !== '')
  {
      for (let i = 0; i < rawPsvResults.length; i++)
      {
          if (localPsvResult === rawPsvResults[i].value)
          {
            selectedPsvResultLabel = rawPsvResults[i].label;
            break;
          }
      }
  }

  return (
    <>
    {editing ?
    <td>
      <Form.Select size="sm" value={localPsvType} onChange={(e) => {handlePsvTypeChanged(e)}} style={{width: "auto"}} >
          {psvTypeList}
      </Form.Select>
    </td>
      :
    <td style={
      !selectedPsvTypeLabel || selectedPsvTypeLabel.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
      }>
        {selectedPsvTypeLabel}
      </td>
    }
    { editing ?
    <td style={{minWidth: '120px'}}>
      <UsStateSelect placeholder="State" originalValue={localState} name="state" onValueChanged={handleStateChanged} small={true}/>
    </td>
    :
    <td>
      {localState}
    </td>
    }
    <td>
    <EditableExpires warnWithColors={false} localExpires={localPsvDate} localActive={true} editingExpires={editing} handleExpiresChanged={handlePsvDateChanged} />
    </td>
    {editing ?
    <td>
      <Form.Select size="sm" value={localPsvResult} onChange={(e) => {handlePsvResultChanged(e)}} style={{width: "auto"}} >
          {psvResultList}
      </Form.Select>
    </td>
      :
    <td style={
      !selectedPsvResultLabel || selectedPsvResultLabel.length === 0
        ? {
            backgroundColor: "rgba(220,53,69,0.3)",
            // cancel the striped bg for this cell
            ["--bs-table-accent-bg"]: "transparent"
          }
        : {}
      }>
        {selectedPsvResultLabel}
      </td>
    }
    {editing ?
    <td style={{minWidth: '150px'}}>
      <Form.Control style={{height: "120px"}} as="textarea" size="sm" maxLength={2048} value={localNotes} onChange={(e) => {handleNotesChanged(e)}} />
    </td>
    :
    <td>
      {localNotes}
    </td>
    }
    {(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))&&
    <>
      {editing && (isH3Admin || authorities.includes("PROVIDER_DOCUMENT_EDIT"))?
      <td>
        <LinkedDocumentSelect entityType={"psv"} providerId={providerId} handleDocumentChanged={handleDocumentChanged} selectedDocuments={selectedDocuments}/>
      </td>
        : 
      <td>
        <LinkedDocumentsDisplay entityType={"psv"} documents={documents} entityId={id}/>
      </td> 
      }
    </>
    }
    <td style={{width: '70px'}}>
      { canEdit?
        <>
        {editing ? 
        <>
          <X style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {cancelEdit(e)}} />
          &nbsp;<Check style={{'height': '16px', 'width': '16px'}} cursor="pointer" onClick={(e) => {saveEdit(e)}} className="text-success"/>
        </>
        :
        <>
          <Edit2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleEditClicked(e)}}/>&nbsp;&nbsp;
          <Trash2 style={{'height': '12px', 'width': '12px'}} cursor="pointer" onClick={(e) => {handleDeleteClicked(e)}}/>
        </>
        }
        </>
        : <>&nbsp;</>
      }
    </td>
  </>
  )
}


const OtherCertificationsTable = ({providerId, addingNew, onCancelAddNew, list, isLoaded, error, canEdit, refreshList}) => {
  const data = list;
  
  const { isH3Admin, authorities } = useContext(UserSettingsContext);

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        accessor: "id",
        key: "id",
      },
      {
        Header: "Type",
        accessor: "psvType",
        key: "psvType",
      },
      {
          Header: "State",
          accessor: "state",
          key: "state",
      },
      {
        Header: "Date",
        accessor: "psvDate",
        key: "psvDate",
      },
      {
        Header: "Result",
        accessor: "psvResult",
        key: "psvResult",
      },
      {
        Header: "Notes",
        accessor: "notes",
        key: "notes",
      }
    ];

    if(isH3Admin || (authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")))
    {
      baseColumns.push(
      {
        Header: () => (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Documents</Tooltip>}
          >
            <span>
              <File size={16} />
            </span>
          </OverlayTrigger>
        ),
        accessor: "documents",
        key: "documents",
      });
    }

    baseColumns.push(
      {
        Header: "",
        accessor: "icons",
        key: "icons",
      },
    );

    return baseColumns;
  }, [isH3Admin, authorities]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
  useTable(
    {
      columns,
      data,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState: {
        hiddenColumns: ['id'],
        sortBy: [
            {
                id: 'psvDate',
                desc: true
            }
        ]
      },
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => caseInsensitiveSort({row1, row2, columnName})
      }
    },
    useFilters,
    useSortBy,
  );

  return (
    <>
      <Table className="table-sm" striped bordered hover {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()} style={{whiteSpace: "nowrap"}}>
            {headerGroup.headers.map((column) => {
              const { key, ...restHeaderProps } = (column.id === "icons" || column.id === "documents" || column.id === "notes") ? column.getHeaderProps() : column.getHeaderProps(column.getSortByToggleProps());
              return (
                <th {...restHeaderProps} key={key}>
                  {column.render("Header")}
                  {column.id !== "icons" && column.id !== "documents" && column.id !== "notes" ? (
                    <>
                    {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon icon={faSortDown} className="ms-2" />
                        ) : (
                          <FontAwesomeIcon icon={faSortUp} className="ms-2" />
                        )
                      ) : (
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                    )}
                    </>
                  ) : <></>
                  }
                </th>
              )
            }
            )}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {
        error ? (<tr><td colSpan={7}>Something went wrong; please reload the page...</td></tr>) : ( 
          !isLoaded ? (<tr><td colSpan={7}>Loading...</td></tr>) : (
            rows.map((row) => {
            prepareRow(row);
            const { key, ...restRowProps } = row.getRowProps();
            return (
              <tr {...restRowProps} key={key}>
                <Psv providerId={providerId} id={row.values.id} key={row.values.id} psvType={row.values.psvType ? row.values.psvType : ""} state={row.values.state ? row.values.state : ""} psvResult={row.values.psvResult ? row.values.psvResult : ""} psvDate={row.values.psvDate} notes={row.values.notes ? row.values.notes : ""} documents={row.values.documents} refreshList={refreshList} canEdit={canEdit}/>
              </tr>
            );
          })))
        }
        {
        addingNew ?
          <tr><Psv providerId={providerId} key={0} startInEditMode={true} psvType={""} state={""} psvResult={""} psvDate={null} notes={""} documents={[]} onCancelEdit={onCancelAddNew} canEdit={canEdit} refreshList={refreshList}/></tr>
        : <></>
        }
      </tbody>
    </Table>
  </>
  )
}

const Psvs = ({providerId, canEdit}) => {
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [data, setData] = useState([]);
  const [addingNew, setAddingNew] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { documentsRefresh, tasksRefresh } = useContext(UserSettingsContext);

  const notyf = useContext(NotyfContext);

  useEffect(() => {
    refreshList();
  }, [providerId, documentsRefresh, tasksRefresh])

  function refreshList()
  {
    setAddingNew(false);
    authenticatedFetch({
      path: "psv/provider/" + providerId,
      successHandler: function(result) {
        setIsLoaded(true);
        setData(result);
      },
      errorHandler: function() {
        setIsLoaded(true);
        setError(true);
      }
    });
  }

  const handleAddNewClicked = (e) => {
    e.preventDefault();
  
    setAddingNew(true);
  }

  const onCancelAddNew = () => {
    setAddingNew(false);
  }

  const handleExportClicked = (e) => {
    if (isExporting) {
      return;
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    e.stopPropagation();
    setIsExporting(true);
    authenticatedFetch({
      method: "GET",
      path:  "report/psv/provider/" + providerId,
      additionalHeaders: {
        "X-Timezone": timezone,
      },
      successHandler: function(result) {
        window.open(result.url);
        setIsExporting(false);
      },
      errorHandler: function(error) {
        setIsExporting(false);
        notyf.open({type: "error", message: "Something went wrong; please try again", duration: 5000, dismissable: true, ripple: true});
      }
    });
  }

  var list = [];
  if (isLoaded)
  {
    list = data.map((item, i) => {
      return {key: item.id, id: item.id, psvType: item.psvType, state: item.state, psvResult: item.psvResult, psvDate: item.psvDate, notes: item.notes, documents: item.documents, updated: item.updated, updatedBy: item.updatedBy}
    }, this);
  }

  return (
    <Card>
      <Card.Header style={{marginBottom: '-20px'}}>
        <Card.Title>Primary Source Verifications
          { isExporting ? 
            <Spinner animation="border" size="sm" variant="primary" className="float-end" style={{marginRight: '12px', marginTop: '7px', marginBottom: '7px'}}/>
            :
            <Form.Label style={{display: "inline", fontSize: "0.8em", fontWeight: "normal", marginTop: "7px", marginRight: "3px"}} className="float-end">
              Export:&nbsp;&nbsp;
              <FontAwesomeIcon onClick={(e) => {handleExportClicked(e)}} icon={faFilePdf} size="lg" style={{color: '#2B7A78', cursor: "pointer"}}/>
              &nbsp;
            </Form.Label>
          }
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <OtherCertificationsTable list={list} addingNew={addingNew} onCancelAddNew={onCancelAddNew} providerId={providerId} error={error} isLoaded={isLoaded} canEdit={canEdit} refreshList={refreshList}/>
        {canEdit?
          <span className="float-end" style={{marginTop: '6px'}}>
            <Button size="sm" variant="primary" className="float-end" onClick={(e) => {handleAddNewClicked(e)}}>+ Add New PSV</Button>
          </span>
          : <></>
          }
      </Card.Body>
    </Card>
  )
}

export default Psvs;
